import { useEffect, useRef, useState, useCallback } from 'react';
import { emptyMeeting, meetingForm, meetingRequest } from '../lib/meetingSummaries';

export const useMeetingSummary = id => {
  const [form, setForm] = useState(emptyMeeting), [doc, setDoc] = useState(null);
  const [baseline, setBaseline] = useState(''), [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const lock = useRef(false), creation = useRef(crypto.randomUUID());
  const dirty = JSON.stringify(form) !== baseline;
  const accept = useCallback(data => {
    const f = meetingForm(data); setForm(f); setDoc(data); setBaseline(JSON.stringify(f));
  }, []);
  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try { accept(await meetingRequest(`/${id}`)); } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id, accept]);
  useEffect(() => {
    const controller = new AbortController(); setError('');
    if (!id) {
      const f = emptyMeeting(); setForm(f); setDoc(null); setBaseline(JSON.stringify(f)); setLoading(false);
      creation.current = crypto.randomUUID(); return;
    }
    setLoading(true);
    meetingRequest(`/${id}`, { signal: controller.signal }).then(data => { if (!controller.signal.aborted) accept(data); })
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, accept]);
  useEffect(() => {
    const warn = e => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const save = async () => {
    if (lock.current) return null;
    lock.current = true; setBusy(true); setError('');
    try {
      const payload = doc ? { ...form, version: doc.version } : { ...form, creation_key: creation.current };
      const data = await meetingRequest(doc ? `/${doc.id}` : '', { method: doc ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      accept(data); return data;
    } catch (e) { setError(e.message); return null; }
    finally { lock.current = false; setBusy(false); }
  };
  return { form, setForm, doc, dirty, loading, busy, error, setError, save, reload };
};